/**
 * Why an account is worth a call today — stated, never scored
 * (docs/tally/18 §11; shown on Today and the account since Collection OS
 * Phase 0, docs/product/phase-0/04).
 *
 * There is no number here. Each rule is one sentence a collector can check
 * against the account: "₹8.4 lakh outstanding", "92 days overdue", "promise
 * missed yesterday", "no contact for 11 days". An account's priority is the
 * most serious reason it has, and the reasons are always shown with it.
 * PRODUCT-UX-FOUNDATION §16 forbids "invented urgency: risk scores, AI
 * priority"; this is the opposite of one — every input is a fact the account
 * already shows, and every threshold is a company setting anyone can read.
 *
 * Age comes from the bills when the source has them (TallyPrime's open bills,
 * after grace: `overdueForCollections`, `oldestOverdueDays`). Without bills —
 * a sheet, or a list row, which carries the account's ageing but not its
 * bills — it comes from the account's ageing bands, and says so in those terms
 * ("more than 90 days old"), never as a count of days it cannot know.
 */
import type { BookRow } from './book.js';
import type { CollectionSettings } from './receivablesSettings.js';
import { daysFrom } from './receivablesFacts.js';
import { chequeState, type Cheque, type ChequeState } from './cheques.js';

export type PriorityLevel = 'high' | 'medium' | 'normal';

export type PriorityReasonCode =
    | 'promise_not_kept' | 'promise_missed' | 'cheque_bounced' | 'critical_overdue' | 'promise_due'
    | 'high_value' | 'overdue' | 'follow_up_overdue' | 'follow_up_today' | 'no_contact' | 'cheque_to_bank';

export interface PriorityReason { code: PriorityReasonCode; level: Exclude<PriorityLevel, 'normal'>; text: string }

/** The account's debit older than each standard age, from its ageing bands (`overdueAgeing`). */
export interface OlderThan { 45: number; 90: number; 135: number }

export interface PriorityInput {
    /** What the customer owes (a credit balance is 0 here: nothing to collect). */
    outstanding: number;
    /** Overdue for collections (after grace), when the source has bills. */
    overdueForCollections: number | null;
    oldestOverdueDays: number | null;
    /** Debit older than 45, 90 and 135 days, read when there are no bills (`overdueForCollections` and `oldestOverdueDays` null). */
    olderThan?: OlderThan | null;
    /** YYYY-MM-DD */
    followUpDate: string | null;
    lastContactDate: string | null;
    /**
     * The account's latest promise. Open: its promised day (when recorded) and
     * amount. Answered "not kept": the promised day is gone from the account
     * (answering clears it), so `answeredOn` says when it was recorded.
     */
    promise: { date: string | null; amount: number | null; outcome: 'kept' | 'not_kept' | null; answeredOn?: string | null } | null;
    paymentRank: 'Good' | 'Late' | 'Bad' | null;
    /** From `chequeState()`. */
    chequeStates: readonly ('due' | 'overdue' | 'upcoming' | 'hold' | 'cleared' | 'bounced')[];
}

export interface Priority {
    level: PriorityLevel;
    reasons: PriorityReason[];
    /** A declared bad debt is recovery, not the daily queue (PRODUCT-UX-FOUNDATION §7). */
    recovery: boolean;
}

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

/** The order reasons are read in, most serious first, within a level. */
const ORDER: readonly PriorityReasonCode[] = [
    'promise_not_kept', 'promise_missed', 'cheque_bounced', 'critical_overdue',
    'promise_due', 'overdue', 'high_value', 'follow_up_overdue', 'follow_up_today', 'cheque_to_bank', 'no_contact',
];

/** The standard ages, oldest first, that an account's ageing bands can speak for. */
const AGES = [135, 90, 45] as const;

/** `day` writes a date as the company reads dates (the screens pass `date.day`); without it, the date as stored. */
export function collectionPriority(
    input: PriorityInput, settings: CollectionSettings, today: string,
    money: (amount: number) => string, day: (date: string) => string = d => d,
): Priority {
    const reasons: PriorityReason[] = [];
    if (!(input.outstanding > 0)) return { level: 'normal', reasons, recovery: false };
    const recovery = input.paymentRank === 'Bad';

    if (input.promise) {
        const what = input.promise.amount ? `Promise of ${money(input.promise.amount)}` : 'Promise';
        if (input.promise.outcome === 'not_kept') {
            reasons.push({
                code: 'promise_not_kept', level: 'high',
                text: input.promise.date
                    ? `${what} for ${day(input.promise.date)} not kept`
                    : `Last promise not kept${input.promise.answeredOn ? ` (recorded ${day(input.promise.answeredOn)})` : ''}`,
            });
        } else if (input.promise.outcome === null && input.promise.date) {
            const past = daysFrom(input.promise.date, today);
            if (past - settings.promiseGraceDays > 0) {
                reasons.push({ code: 'promise_missed', level: 'high', text: `${what} for ${day(input.promise.date)} unanswered, ${plural(past - settings.promiseGraceDays, 'day')} past its date` });
            } else if (past === 0) {
                reasons.push({ code: 'promise_due', level: 'medium', text: `${what} due today` });
            }
        }
    }
    if (input.chequeStates.includes('bounced')) reasons.push({ code: 'cheque_bounced', level: 'high', text: 'A cheque bounced' });

    if (input.oldestOverdueDays !== null || input.overdueForCollections !== null) {
        // The bills say how late, to the day.
        if (input.oldestOverdueDays !== null && input.oldestOverdueDays >= settings.criticalOverdueDays) {
            reasons.push({ code: 'critical_overdue', level: 'high', text: `${plural(input.oldestOverdueDays, 'day')} overdue (critical from ${settings.criticalOverdueDays})` });
        } else if (input.overdueForCollections !== null && input.overdueForCollections > 0) {
            reasons.push({ code: 'overdue', level: 'medium', text: `${money(input.overdueForCollections)} overdue` });
        }
    } else if (input.olderThan) {
        // The bands say "older than", never a day count. Critical only where a
        // band lies wholly past the company's threshold: with "critical from
        // 100 days", money "more than 90 days old" may be 95 days old, so only
        // the over-135 band can speak for it.
        const critical = [...AGES].reverse().find(age => age >= settings.criticalOverdueDays && input.olderThan![age] > 0);
        if (critical !== undefined) {
            reasons.push({ code: 'critical_overdue', level: 'high', text: `${money(input.olderThan[critical])} more than ${critical} days old (critical from ${settings.criticalOverdueDays} days)` });
        } else if (input.olderThan[45] > 0) {
            const oldest = AGES.find(age => input.olderThan![age] > 0)!;
            reasons.push({ code: 'overdue', level: 'medium', text: `${money(input.olderThan[oldest])} more than ${oldest} days old` });
        }
    }

    if (settings.highValueAmount !== null && input.outstanding >= settings.highValueAmount) {
        reasons.push({ code: 'high_value', level: 'medium', text: `${money(input.outstanding)} outstanding (high value from ${money(settings.highValueAmount)})` });
    }
    if (input.followUpDate) {
        const d = daysFrom(input.followUpDate, today);
        if (d > 0) reasons.push({ code: 'follow_up_overdue', level: 'medium', text: `Follow-up was due ${plural(d, 'day')} ago` });
        else if (d === 0) reasons.push({ code: 'follow_up_today', level: 'medium', text: 'Follow-up due today' });
    }
    const silent = input.lastContactDate ? daysFrom(input.lastContactDate, today) : null;
    if (silent === null) reasons.push({ code: 'no_contact', level: 'medium', text: 'No contact recorded' });
    else if (silent >= settings.staleContactDays) reasons.push({ code: 'no_contact', level: 'medium', text: `No contact for ${plural(silent, 'day')}` });
    if (input.chequeStates.some(s => s === 'due' || s === 'overdue')) reasons.push({ code: 'cheque_to_bank', level: 'medium', text: 'A cheque is due to be banked' });

    reasons.sort((a, b) => (a.level === b.level ? ORDER.indexOf(a.code) - ORDER.indexOf(b.code) : a.level === 'high' ? -1 : 1));
    const level: PriorityLevel = reasons.some(r => r.level === 'high') ? 'high' : reasons.length ? 'medium' : 'normal';
    return { level, reasons, recovery };
}

/**
 * The facts one row of the book carries, as `collectionPriority` reads them —
 * one adapter, so Today's row and the account's page give the same reasons.
 *
 * `day` writes a moment or a date as the company's calendar day, YYYY-MM-DD
 * (the screens pass `date.inputValue`). A list row has the account's ageing
 * but not its bills, so age comes from the bands. A promise's day is used only
 * when it was recorded: nothing is said about a day nobody gave.
 */
export function priorityInputFromBookRow(
    row: BookRow, day: (value: Date | string) => string,
    chequeStates: PriorityInput['chequeStates'] = [],
): PriorityInput {
    const c = row.customer;
    const owes = !row.settled && c.totalType !== 'Cr' && c.total > 0;
    const promise: PriorityInput['promise'] = row.promise
        ? { date: c.forecastDate ? day(c.forecastDate) : null, amount: row.promise.amount, outcome: null }
        : c.promiseOutcome === 'not_kept'
            ? { date: null, amount: null, outcome: 'not_kept', answeredOn: c.promiseOutcomeOn ? day(c.promiseOutcomeOn) : null }
            : null;
    return {
        outstanding: owes ? c.total : 0,
        overdueForCollections: null,
        oldestOverdueDays: null,
        olderThan: { 45: row.overdue.over45, 90: row.overdue.over90, 135: row.overdue.over135 },
        followUpDate: c.followUpDate ? day(c.followUpDate) : null,
        lastContactDate: c.lastFollowUpOn ? day(c.lastFollowUpOn) : null,
        promise,
        paymentRank: row.rank,
        chequeStates,
    };
}

/**
 * A bounced cheque is a reason while it is recent: one that bounced last year,
 * on an account that has paid since, is history, not a reason to call today.
 */
export const BOUNCED_RECENT_DAYS = 90;

/**
 * The cheque states that speak for an account today: every cheque due or
 * overdue to bank, and a bounce dated within `BOUNCED_RECENT_DAYS`.
 */
export function priorityChequeStates(
    cheques: readonly Pick<Cheque, 'status' | 'chequeDate'>[], today: Date,
): ChequeState[] {
    const recentFrom = new Date(today);
    recentFrom.setHours(0, 0, 0, 0);
    recentFrom.setDate(recentFrom.getDate() - BOUNCED_RECENT_DAYS);
    return cheques
        .filter(c => chequeState(c, today) !== 'bounced' || new Date(c.chequeDate).getTime() >= recentFrom.getTime())
        .map(c => chequeState(c, today))
        .filter(s => s === 'due' || s === 'overdue' || s === 'bounced');
}

/** Highest priority first; within a level, more serious reasons, then more overdue, then more outstanding. Never a hidden weight. */
export function comparePriority(
    a: { priority: Priority; overdue: number; outstanding: number },
    b: { priority: Priority; overdue: number; outstanding: number },
): number {
    const rank = (p: Priority) => (p.recovery ? 3 : p.level === 'high' ? 0 : p.level === 'medium' ? 1 : 2);
    return rank(a.priority) - rank(b.priority)
        || b.priority.reasons.filter(r => r.level === 'high').length - a.priority.reasons.filter(r => r.level === 'high').length
        || b.overdue - a.overdue
        || b.outstanding - a.outstanding;
}
